/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import React from 'react';
import { inject, observer } from 'mobx-react';
import Framework, { shapes, LoadingHandler, StateErrorDisplay } from '@greenville/framework';
import PropTypes from 'prop-types';
import PdfViewer from './components/PdfViewer';
import Asset from './model/Asset';
import EpubAsset from './model/EpubAsset';
import Loader from '../../common/Loader';
import Notification from '../../common/components/notification/Notification';
import MediaBasepaths from '../books/models/MediaBasepaths';
import InterfaceColors from '../books/models/InterfaceModel';
import ValidatePermissions from '../users/ValidatePermissions';
import Books from '../books/models/Books';
import * as constants from '../../common/constants';
import ResourcePanel from './model/Resources';
import {
  fetchPxeTocForBookDetail,
  getFirstPxePageId,
  hasPxePreviewContent,
  isPxeTitleType
} from './common/PxePreviewUtils';
import {
  getFirstRplusEpubPageId,
  isRplusEpubTitleType,
  loadRplusEpubAssettocIfNeeded
} from './common/RplusEpubPreviewUtils';
import { showAppHeader, syncAppHeaderVisibilityForPreview } from '../../common/header/appHeaderVisibility';

@inject(
  'asset',
  'epubAsset',
  'booksState',
  'tocStatus',
  'epubTocStatus',
  'pxeTocStatus',
  'basepath',
  'mediaBasepathStatus',
  'colorInterface',
  'resourcePanel'
)
@observer
class PdfViewerContainer extends React.Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    asset: shapes.modelOf(Asset).isRequired,
    epubAsset: shapes.modelOf(EpubAsset).isRequired,
    tocStatus: shapes.state.isRequired,
    epubTocStatus: shapes.state.isRequired,
    pxeTocStatus: shapes.state.isRequired,
    booksState: shapes.modelOf(Books).isRequired,
    basepath: shapes.modelOf(MediaBasepaths).isRequired,
    mediaBasepathStatus: shapes.state.isRequired,
    colorInterface: shapes.modelOf(InterfaceColors).isRequired,
    resourcePanel: shapes.modelOf(ResourcePanel).isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      resources: []
    };
    syncAppHeaderVisibilityForPreview(props.booksState?.bookDetail?.titleType);
  }

  componentDidMount() {
    const { asset, match, basepath, colorInterface, booksState } = this.props;
    const { bookDetail } = booksState;
    this.syncAppHeaderVisibility();
    colorInterface.fetch(match.params.bookid);
    if (
      bookDetail.titleType === 'PDF' ||
      bookDetail.titleType === 'ETEXT_PDF' ||
      bookDetail.titleType === 'RPLUS_PDF'
    ) {
      asset.fetch(match.params.bookid);
    } else if (bookDetail.titleType === 'EPUB') {
      asset.fetch(match.params.bookid);
    } else if (isRplusEpubTitleType(bookDetail.titleType)) {
      this.loadRplusEpubPreviewAsset();
    } else if (isPxeTitleType(bookDetail.titleType)) {
      this.loadPxePreviewAsset();
    } else {
      booksState.fetchBook(match.params.bookid, true);
      Framework.getEventManager().on(constants.BOOK_USECALLBACK, () => {
        if (
          bookDetail.titleType === 'PDF' ||
          bookDetail.titleType === 'ETEXT_PDF' ||
          bookDetail.titleType === 'RPLUS_PDF'
        ) {
          asset.fetch(match.params.bookid);
        } else if (bookDetail.titleType === 'EPUB') {
          asset.fetch(match.params.bookid);
        } else if (isRplusEpubTitleType(bookDetail.titleType)) {
          this.loadRplusEpubPreviewAsset();
        } else if (isPxeTitleType(bookDetail.titleType)) {
          this.loadPxePreviewAsset();
        }
      });
    }
    basepath.fetch(match.params.bookid);
  }

  componentDidUpdate(prevProps) {
    const { booksState } = this.props;
    const prevPulseBookID = prevProps.booksState.bookDetail.pulseBookID;
    const nextPulseBookID = booksState.bookDetail.pulseBookID;
    const prevBasePath = prevProps.booksState.bookDetail.basePath;
    const nextBasePath = booksState.bookDetail.basePath;
    if (
      isPxeTitleType(booksState.bookDetail.titleType) &&
      nextPulseBookID &&
      nextBasePath &&
      (nextPulseBookID !== prevPulseBookID || nextBasePath !== prevBasePath)
    ) {
      this.loadPxePreviewAsset();
    }
    if (isRplusEpubTitleType(booksState.bookDetail.titleType) && nextBasePath && nextBasePath !== prevBasePath) {
      this.loadRplusEpubPreviewAsset();
    }
    if (prevProps.booksState.bookDetail.titleType !== booksState.bookDetail.titleType) {
      this.syncAppHeaderVisibility();
    }
  }

  componentWillUnmount() {
    showAppHeader(true);
  }

  getPreviewStatus = (isPxe, isRplusEpub) => {
    const { pxeTocStatus, epubTocStatus, tocStatus } = this.props;
    if (isPxe) {
      return pxeTocStatus;
    }
    if (isRplusEpub) {
      return epubTocStatus;
    }
    return tocStatus;
  };

  loadPxePreviewAsset = () => {
    const { asset, match, booksState } = this.props;
    const { bookDetail } = booksState;
    if (!isPxeTitleType(bookDetail.titleType) || !bookDetail.pulseBookID || !bookDetail.basePath) {
      return;
    }
    fetchPxeTocForBookDetail(asset, bookDetail, match.params.bookid);
  };

  loadRplusEpubPreviewAsset = () => {
    const { epubAsset, epubTocStatus, match, booksState } = this.props;
    loadRplusEpubAssettocIfNeeded(epubAsset, booksState.bookDetail, match.params.bookid, epubTocStatus);
  };

  syncAppHeaderVisibility() {
    const { booksState } = this.props;
    syncAppHeaderVisibilityForPreview(booksState.bookDetail.titleType);
  }

  render() {
    const { mediaBasepathStatus, asset, epubAsset, match, booksState } = this.props;
    const { resources } = this.state;
    const { pageid, bookid } = match.params;
    const { titleType } = booksState.bookDetail;
    const isPxe = isPxeTitleType(titleType);
    const isRplusEpub = isRplusEpubTitleType(titleType);
    const previewStatus = this.getPreviewStatus(isPxe, isRplusEpub);
    const previewLoading = previewStatus.isPending;
    const previewError = previewStatus.error;
    // RPLUS empty TOC is gated on Book Detail; PXE still surfaces empty TOC here.
    const isPxeEmptyToc = isPxe && !previewLoading && !previewError && asset.bookId && !hasPxePreviewContent(asset);
    const displayPreviewError = isPxeEmptyToc
      ? { status: 404, message: constants.PXE_EMPTY_TOC_ERROR_MSG }
      : previewError;
    if (pageid === undefined) {
      let firstPageId;
      if (isPxe && asset.bookId) {
        firstPageId = getFirstPxePageId(asset);
      } else if (isRplusEpub && epubAsset.bookId) {
        firstPageId = getFirstRplusEpubPageId(epubAsset);
      } else if (asset.bookId) {
        firstPageId = asset.slates.length && asset.slates[0].playOrder;
      }
      if (firstPageId != null) {
        Framework.redirectTo(`/books/${bookid}/preview/${firstPageId}`);
      }
    }

    const isEmptyToc = isPxeEmptyToc;

    return (
      <ValidatePermissions givenPermissions="authoring_can_view" fallElse>
        <LoadingHandler
          loading={previewLoading || mediaBasepathStatus.isPending}
          isError={
            !!isEmptyToc ||
            (!!displayPreviewError && !previewLoading) ||
            (mediaBasepathStatus.isError && mediaBasepathStatus.error && mediaBasepathStatus.error.status === 401)
          }
          loadingContent={<Loader />}
          errorContent={<StateErrorDisplay error={displayPreviewError || mediaBasepathStatus.error} showDetails />}
          content={
            !isEmptyToc && (
              <>
                <Notification />
                <PdfViewer {...this.props} resources={resources} />
              </>
            )
          }
        />
      </ValidatePermissions>
    );
  }
}

export default PdfViewerContainer;
